import type { PreviewPlatform } from './PhoneFrame.tsx';

/** The fields a card preview needs: the content part of the SDK card (`@bb/contracts` `sdk.Card`). */
export interface CardPreviewData {
  type: 'image_only' | 'captioned_image' | 'classic';
  title?: string;
  description?: string;
  linkText?: string;
  image?: { url: string; aspectRatio: number; alt?: string };
  pinned: boolean;
  dismissible: boolean;
}

/**
 * One Content Card as the VCG feed draws it (BB-61):
 * - Image Only — the image at its own aspect ratio, nothing else;
 * - Captioned Image — the image, then title, description and link text;
 * - Classic — an optional square thumbnail beside title, description and link text.
 * Titles stay on one line and descriptions on three, as in the app. Pinned cards show a pin; dismissible ones a cross.
 */
export function CardPreview({ card, platform }: { card: CardPreviewData; platform: PreviewPlatform }) {
  const image = card.image ? <img className="bb-card__image" src={card.image.url} alt={card.image.alt ?? ''} style={{ aspectRatio: String(card.image.aspectRatio) }} /> : null;
  const text = (
    <div className="bb-card__text">
      {card.title && <p className="bb-card__title">{card.title}</p>}
      {card.description && <p className="bb-card__description">{card.description}</p>}
      {card.linkText && <p className="bb-card__link">{card.linkText}</p>}
    </div>
  );
  return (
    <article className={`bb-card bb-card--${card.type} bb-card--${platform}`} aria-label={`${card.type.replace('_', ' ')} card${card.title ? `: ${card.title}` : ''}`} data-aspect={card.image?.aspectRatio}>
      {(card.pinned || card.dismissible) && (
        <div className="bb-card__badges">
          {card.pinned && (
            <span className="bb-card__pin" role="img" aria-label="Pinned">
              📌
            </span>
          )}
          {card.dismissible && (
            <span className="bb-card__dismiss" role="img" aria-label="Dismissible">
              ×
            </span>
          )}
        </div>
      )}
      {card.type === 'image_only' && image}
      {card.type === 'captioned_image' && (
        <>
          {image}
          {text}
        </>
      )}
      {card.type === 'classic' && (
        <div className="bb-card__row">
          {card.image && <img className="bb-card__thumb" src={card.image.url} alt={card.image.alt ?? ''} />}
          {text}
        </div>
      )}
    </article>
  );
}

/** A feed of cards in order: pinned first, as the SDKs sort them. */
export function CardFeedPreview({ cards, platform }: { cards: CardPreviewData[]; platform: PreviewPlatform }) {
  const sorted = [...cards.filter((c) => c.pinned), ...cards.filter((c) => !c.pinned)];
  return (
    <div className="bb-card-feed" role="list" aria-label="Card feed">
      {sorted.map((card, i) => (
        <div role="listitem" key={i}>
          <CardPreview card={card} platform={platform} />
        </div>
      ))}
    </div>
  );
}
